import type { ColumnConfig } from '../types';
import styles from './DataTable.module.css';

interface DataTableProps<T extends object> {
  data: T[];
  columns: ColumnConfig<T>[];
  keyField: keyof T;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
}

export function DataTable<T extends object>({
  data,
  columns,
  keyField,
  emptyMessage = 'No data available',
  onRowClick,
}: DataTableProps<T>) {
  const getValue = (item: T, key: string): unknown => {
    const keys = key.split('.');
    let value: unknown = item;
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = (value as Record<string, unknown>)[k];
      } else {
        return undefined;
      }
    }
    return value;
  };

  const renderCell = (item: T, column: ColumnConfig<T>) => {
    const value = getValue(item, column.key as string);

    if (column.render) {
      return column.render(value, item);
    }

    if (value === null || value === undefined) {
      return <span className={styles.empty}>-</span>;
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  };

  if (data.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key as string}
                style={{ width: column.width }}
                className={styles.th}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={`${String(item[keyField])}-${index}`}
              onClick={() => onRowClick?.(item)}
              className={`${styles.tr} ${onRowClick ? styles.clickable : ''}`}
            >
              {columns.map((column) => (
                <td key={column.key as string} className={`${styles.td} ${column.className || ''}`}>
                  {renderCell(item, column)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
